import { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { apiRequest, listArtifacts } from '@/lib/api'
import type { CollaborationStatusKey } from '@/lib/section-status'
import { OverviewStatTile } from '@/components/overview/OverviewStatTile'

export interface SectionAssignment {
  sectionId: string
  role: 'writer' | 'reviewer'
  collaborationStatus: CollaborationStatusKey
}

const ATTENTION_STATUSES = new Set<string>(['changes_requested', 'in_review', 'not_started'])

export interface SectionProgress {
  total: number
  /** Sections with no writer, not started, in review or sent back. */
  attention: number
}

/**
 * Section progress for one or more documents: every section counts, and a
 * section needs attention when it has no writer or its writer's status is
 * not started, in review or changes requested.
 */
export function summariseSectionProgress(
  sections: readonly { sectionKey: string }[],
  assignments: readonly SectionAssignment[]
): SectionProgress {
  const writerBySection = new Map<string, SectionAssignment>()
  for (const a of assignments) {
    if (a.role === 'writer') writerBySection.set(a.sectionId, a)
  }
  let attention = 0
  for (const section of sections) {
    const assignment = writerBySection.get(section.sectionKey)
    const status = assignment?.collaborationStatus ?? 'not_started'
    if (!assignment || ATTENTION_STATUSES.has(status)) attention++
  }
  return { total: sections.length, attention }
}

export function ResponseProgress({ bidId }: { bidId: string }) {
  const [totalSections, setTotalSections] = useState(0)
  const [attentionCount, setAttentionCount] = useState(0)
  const [hasArtifacts, setHasArtifacts] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const { artifacts } = await listArtifacts(bidId)
        if (cancelled) return
        setHasArtifacts(artifacts.length > 0)
        if (artifacts.length === 0) return

        const { assignments } = await apiRequest<{ assignments: SectionAssignment[] }>(
          `/artifacts/${artifacts[0]!.id}/assignments`
        )
        if (cancelled) return

        const { total, attention } = summariseSectionProgress(
          artifacts.flatMap((artifact) => artifact.outlineJson?.sections ?? []),
          assignments
        )

        setTotalSections(total)
        setAttentionCount(attention)
      } catch {
        // Overview is supplementary — fail silently.
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [bidId])

  // The empty case is exactly when a first-run user needs pointing somewhere, so
  // the tile stays put and swaps its sub line for the next step rather than
  // hiding itself.
  const emptyHint = hasArtifacts ? 'Add sections to start drafting' : 'Create a response to start'
  const settledSub =
    totalSections === 0 ? emptyHint : attentionCount > 0 ? `${attentionCount} need attention` : 'sections drafting'

  return (
    <OverviewStatTile
      icon={FileText}
      label="Response progress"
      value={totalSections}
      // `loading` only gates the tile's value, so the sub line has to be held
      // back here too: mid-fetch the counts are still zero, and a bid that
      // already has responses would otherwise flash "Create a response to
      // start" on every load.
      sub={loading ? undefined : settledSub}
      progress={totalSections > 0 ? ((totalSections - attentionCount) / totalSections) * 100 : undefined}
      to={`/app/bids/${bidId}/write`}
      loading={loading}
    />
  )
}
